// CORE-501 — «Пройти знакомство с платформой» inside Чаты → Agapella.
//
// The whole tour is mirrored into this chat, so the chat is also where the user
// comes back to re-run it. Pressing it returns to the home page (the tour never
// runs anywhere else) and restarts from step 1. Unlimited replays by spec.
//
// Renders nothing outside web, outside the default assistant's chat, or when the
// tour is gated off.
//
// QA (dev): «в актуальном DOM компактного окна отсутствует обязательная по
// описанию кнопка "Пройти знакомство с платформой"». It was there — as an
// accessibilityLabel on a bare ✦ glyph in the pop-up header, which is not a
// control anyone can find. The pop-up now shows this same named button, in a
// `compact` rhythm that fits 360 px.
//
// CORE-1750 (возврат QA) — крупная кнопка только пока тур не пройден. Кто хоть
// раз завершил или пропустил, видит на её месте тихую ссылку «Пройти
// знакомство ещё раз» (TourReplayLink ниже): повтор тот же, с первого шага, но
// в тур его больше не зовут. Что показать, решает tourReplayEntry по истории
// тура с сервера. Оба места — полный чат и компактное окно — рисуют этот
// компонент, поэтому правило в них одно.
//
// CORE-1750 (возврат QA 23.10) — кнопка стоит на поверхности темы. Заливка
// `rgba(255, 247, 241, 0.72)` была светлой в обеих темах (CORE-974), а подпись
// в тёмной с CORE-1733 — светлый акцент #F0A06D: 1,13:1 у QA в полном чате и
// в окне, на 1512 и на 390. Теперь подложка — поверхность v4 (`surfaceFill`),
// подпись — акцентный текст темы (`ACCENT_TEXT`); подсветка наведения и
// нажатия — слой поверх градиента поверхности, а не цвет под ним: под
// непрозрачным градиентом `background-color` не виден. Подпись — из словаря,
// как у ссылки.
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { ONBOARDING_TOUR_ENABLED } from '../config/flags';
import { navigationRef } from '../navigation/rootNavigation';
import { typographyTokens } from '../styles/typographyTokens';
import { PlatformTheme } from '../styles/platformBackgrounds';
import { tourCtaFill } from './tourCtaFill';
import { useOnboardingTour } from './OnboardingTourProvider';
import { tourReplayEntry } from './tourReplayEntry';

import { ACCENT_TEXT } from '../styles/accentText';

export const TourReplayBanner: React.FC<{
  /** Tighter rhythm for the 360 px widget pop-up; the full chat keeps its own. */
  compact?: boolean;
}> = ({ compact }) => {
  const { colors, isDark } = useTheme();
  const { t } = useI18n('chats');
  const tour = useOnboardingTour();

  if (!ONBOARDING_TOUR_ENABLED || Platform.OS !== 'web' || !tour) return null;

  const entry = tourReplayEntry(tour.history);
  if (!entry) return null;

  const replay = () => {
    if (navigationRef.isReady?.()) {
      (navigationRef.navigate as unknown as (n: string) => void)('Home');
    }
    // Let the home layout (and the tour anchors) mount before spotlighting.
    setTimeout(() => tour.replay(), 350);
  };

  if (entry === 'link') return <TourReplayLink compact={compact} onPress={replay} />;

  const label = t('tour.replayCta');
  const theme: PlatformTheme = isDark ? 'dark' : 'light';

  return (
    <View
      style={[
        styles.root,
        compact && styles.rootCompact,
        { borderBottomColor: colors.borderSubtle },
      ]}
    >
      <Pressable
        onPress={replay}
        accessibilityRole="button"
        accessibilityLabel={label}
        // Web tooltip — the third carrier of the spec wording, alongside the
        // visible label and the a11y name.
        {...(Platform.OS === 'web' ? ({ title: label } as object) : null)}
        testID="agapella-tour-replay"
        style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
          styles.btn,
          compact && styles.btnCompact,
          tourCtaFill(theme, pressed ? 'pressed' : hovered ? 'hover' : 'rest', colors.solar),
          { opacity: pressed ? 0.9 : 1 },
        ]}
      >
        <Ionicons name="sparkles-outline" size={compact ? 14 : 16} color={ACCENT_TEXT} />
        <Text
          numberOfLines={1}
          style={[styles.label, compact && styles.labelCompact, { color: ACCENT_TEXT }]}
        >
          {label}
        </Text>
      </Pressable>
    </View>
  );
};

/**
 * CORE-1750 — повтор для того, кто тур уже прошёл. Самая тихая форма из
 * имеющихся: текст-ссылка без рамки, заливки и тени, приглушённым цветом;
 * тёплый акцент — только под курсором и в фокусе, как у кнопок шапки окна
 * (WidgetIconButton). Стоит справа, как действие шапки, а не поперёк
 * переписки. Меню у треда Агапеллы и у компактного окна нет, а заводить его
 * ради одного пункта — новая шапка, а не тихий повтор.
 */
const TourReplayLink: React.FC<{ compact?: boolean; onPress: () => void }> = ({ compact, onPress }) => {
  const { colors } = useTheme();
  const { t } = useI18n('chats');
  const [active, setActive] = useState(false);
  const label = t('tour.replayAgain');
  const color = active ? colors.solar : colors.textMuted;

  return (
    <View
      style={[
        styles.linkRow,
        // В компактном окне строка стоит над телом окна, на поверхности самого
        // окна, а переписка под ней — на своей. Без своего фона в тёмной теме
        // строка читалась полосой шапки; с фоном переписки она часть чата, как
        // и в полном чате, где ссылка стоит внутри треда.
        compact && [styles.linkRowCompact, { backgroundColor: colors.surfacePrimary }],
      ]}
    >
      <Pressable
        onPress={onPress}
        onHoverIn={() => setActive(true)}
        onHoverOut={() => setActive(false)}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        hitSlop={6}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID="agapella-tour-replay-again"
        style={[
          styles.link,
          { cursor: 'pointer', transition: 'color 160ms ease' } as any,
        ]}
      >
        <Ionicons name="sparkles-outline" size={14} color={color} />
        <Text numberOfLines={1} style={[styles.linkLabel, { color }]}>
          {label}
        </Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 48,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1.5,
    shadowColor: '#D66B43',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 18,
  },
  label: { fontSize: 14, fontWeight: '600' },
  // The pop-up is 360 px wide; the chat's rhythm leaves the label cramped there.
  rootCompact: { paddingHorizontal: 12, paddingVertical: 8 },
  btnCompact: { minHeight: 42, gap: 6, paddingHorizontal: 16, paddingVertical: 8 },
  labelCompact: { fontSize: 13 },
  linkRow: { flexDirection: 'row', justifyContent: 'flex-end', paddingHorizontal: 16, paddingTop: 6 },
  linkRowCompact: { paddingHorizontal: 12, paddingTop: 4 },
  link: { flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 32 },
  linkLabel: { ...typographyTokens.link },
});
